'use client';
import {
  Autocomplete,
  autocompleteClasses,
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  iconButtonClasses,
  Typography,
} from '@castlery/fortress';
import { ArrowRight, Close } from '@castlery/fortress/Icons';
import { useState } from 'react';
import { useDebounce } from 'react-use';
// import { postcodeValidator, postcodeValidatorExistsForCountry } from 'postcode-validator';

export enum LocationSearchType {
  ZIPCODE = 'zipcode',
  ADDRESS = 'address',
}
export interface SearchListOption {
  label: string;
  value: string;
  rawData?: any;
}

export function PureLocationSearch({
  type,
  placeholder,
  autoFocus = false,
  searchHandler,
  onSelect,
  onSubmit,
  errorHelperText,
  onClear,
}: {
  type: LocationSearchType;
  placeholder?: string;
  autoFocus?: boolean;
  searchHandler: (query: string) => Promise<SearchListOption[]>;
  onSelect: (option: SearchListOption) => Promise<Record<string, any> | null>;
  onSubmit: (payload: Record<string, any> | null) => Promise<void>;
  errorHelperText?: string;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<SearchListOption[]>([]);
  const [searchKeyWords, setSearchKeyWords] = useState('');
  const [selectedOption, setSelectedOption] = useState<SearchListOption | null>(null);
  const [submitPayload, setSubmitPayload] = useState<any>(null);
  const [inputValue, setInputValue] = useState('');

  const isReadyToSearch = (value: string) => {
    if (type === LocationSearchType.ZIPCODE) {
      return value?.length > 2;
    } else {
      return value?.length > 0;
    }
  };

  const submitHandler = async (payload: Record<string, any> | null) => {
    if (submitLoading || loading) return Promise.resolve();
    setSubmitLoading(true);
    await onSubmit(payload);
    setSubmitLoading(false);
  };

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (submitLoading || loading) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      if (!selectedOption && filterOptions.length === 1) {
        setSelectedOption(filterOptions[0]);
        setOpen(false);
        if (filterOptions[0].value === 'address_not_found_fallback') {
          await submitHandler(null);
          return;
        }
        const result = await onSelect(filterOptions[0]);
        setSubmitPayload(result);
        await submitHandler(result);
      } else if (!selectedOption && filterOptions.length === 0) {
        setOpen(false);
        await submitHandler(null);
      }
    }
  };

  // 搜索地址时，使用debounce，避免频繁请求
  useDebounce(
    async () => {
      setLoading(true);
      setFilterOptions([]);
      setSelectedOption(null);
      setSubmitPayload(null);
      const result = await searchHandler(searchKeyWords);
      setLoading(false);
      setFilterOptions(result);
      setOpen(() => result?.length > 0);
    },
    600,
    [searchKeyWords]
  );

  const handleInputChange = (event: React.SyntheticEvent, value: string, reason: string) => {
    setInputValue(value);
    if (reason === 'input') {
      if (isReadyToSearch(value)) {
        setLoading(true);
        setSearchKeyWords(value);
      }
    }
    if (value?.length === 0) {
      handleClear();
    }
  };

  const handleAutocompleteChange = async (
    event: React.SyntheticEvent,
    value: SearchListOption | Array<SearchListOption> | null,
    reason: string
  ) => {
    if (value && !Array.isArray(value) && reason === 'selectOption') {
      if (value.value === 'address_not_found_fallback') {
        setOpen(false);
        await submitHandler(null);
        return;
      }
      setSelectedOption(value);
      setOpen(false);
      const result = await onSelect(value);
      setSubmitPayload(result || null);
    }
  };

  const handleBlur = () => {
    setOpen(false);
  };

  const handleClear = () => {
    setSelectedOption(null);
    setInputValue('');
    setSearchKeyWords('');
    setFilterOptions([]);
    setSubmitPayload(null);
    setOpen(false);
    onClear();
  };
  return (
    <Box position="relative" mb={0.5} sx={{ maxWidth: 692 }}>
      <FormControl error={!!errorHelperText}>
        <form onSubmit={(e) => e.preventDefault()} onKeyDown={handleKeyDown} action="/">
          <Autocomplete
            variant="outlined"
            openOnFocus={false}
            open={open && !loading}
            onClose={() => setOpen(false)}
            value={selectedOption}
            onChange={handleAutocompleteChange}
            clearIcon={
              <IconButton sx={{ width: 32, height: 32 }} onClick={handleClear}>
                <Close />
              </IconButton>
            }
            clearOnBlur={false}
            forcePopupIcon={false}
            inputValue={inputValue}
            placeholder={placeholder}
            autoFocus={autoFocus}
            onInputChange={handleInputChange}
            onBlur={handleBlur}
            options={filterOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              return option.value === value.value;
            }}
            filterOptions={(options) => options}
            endDecorator={
              <IconButton
                variant="primary"
                size="sm"
                loading={submitLoading}
                disabled={inputValue?.length <= 0}
                onClick={() => submitHandler(submitPayload)}
                sx={{
                  width: 32,
                  height: 32,
                  [`& .${iconButtonClasses.loadingIndicator}`]: {
                    width: 20,
                  },
                }}
              >
                <ArrowRight />
              </IconButton>
            }
            sx={{
              pr: 16,
              minWidth: 300,
              height: 52,
              alignItems: 'center',
              background: 'transparent',
              [`& .${autocompleteClasses.endDecorator}`]: {
                width: 32,
                height: 32,
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                margin: 'auto',
              },
            }}
          />
        </form>
        {loading && (
          <Typography level="caption2" mt={1} pl={1}>
            Loading...
          </Typography>
        )}
        {!!errorHelperText && !loading && <FormHelperText>{errorHelperText}</FormHelperText>}
      </FormControl>
    </Box>
  );
}

export default PureLocationSearch;
